/**
 * PillTabs — Base UI Tabs primitive (top-level pill style).
 *
 * Pair with <UnderlineTabs> for sub-level tabs nested under a section.
 *
 * Design system mapping:
 *   • List: inline-flex gap-1 p-1 rounded-full
 *   • Tab:  h-8 px-3 rounded-3xl text-sm font-medium (Webex tabs: 14/500/21px)
 *           default: text-webex-gray-700 dark: webex-gray-300
 *           hover:   bg webex-gray-100 dark: webex-gray-850
 *           active:  bg webex-gray-200 dark: webex-gray-800 + text full
 *   • Panel: outline-none focus-visible ring
 *
 * Composition:
 *   <PillTabs.Root defaultValue="overview">
 *     <PillTabs.List>
 *       <PillTabs.Tab value="overview">Overview</PillTabs.Tab>
 *       <PillTabs.Tab value="members">Members</PillTabs.Tab>
 *     </PillTabs.List>
 *     <PillTabs.Panel value="overview">...</PillTabs.Panel>
 *     <PillTabs.Panel value="members">...</PillTabs.Panel>
 *   </PillTabs.Root>
 */
import * as React from "react"
import { Tabs as BaseTabs } from "@base-ui-components/react/tabs"
import { cn } from "@lib/cn"

const Root = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.Root>
>(({ className, ...props }, ref) => (
  <BaseTabs.Root ref={ref} className={cn("flex flex-col gap-4", className)} {...props} />
))
Root.displayName = "PillTabs.Root"

const List = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.List>
>(({ className, ...props }, ref) => (
  <BaseTabs.List
    ref={ref}
    className={cn("inline-flex gap-1 p-1 rounded-full", className)}
    {...props}
  />
))
List.displayName = "PillTabs.List"

const Tab = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.Tab>
>(({ className, ...props }, ref) => (
  <BaseTabs.Tab
    ref={ref}
    className={cn(
      "inline-flex h-8 items-center gap-1.5 px-3.5 rounded-[24px]",
      "text-sm font-medium leading-tight",
      "text-webex-gray-700 dark:text-webex-gray-300",
      "bg-transparent border-0 cursor-pointer select-none",
      "transition-colors duration-150 motion-reduce:transition-none",
      "hover:bg-webex-gray-100 hover:text-webex-gray-1000 dark:hover:bg-webex-gray-850 dark:hover:text-white",
      "aria-selected:bg-webex-gray-200 aria-selected:text-webex-gray-1000",
      "dark:aria-selected:bg-webex-gray-800 dark:aria-selected:text-white",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950",
      "[&>svg]:size-4 [&>img]:size-4",
      className,
    )}
    {...props}
  />
))
Tab.displayName = "PillTabs.Tab"

const Panel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.Panel>
>(({ className, ...props }, ref) => (
  <BaseTabs.Panel
    ref={ref}
    className={cn(
      "outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950 rounded",
      className,
    )}
    {...props}
  />
))
Panel.displayName = "PillTabs.Panel"

export const PillTabs = { Root, List, Tab, Panel, Indicator: BaseTabs.Indicator }
